import { useQuery } from '@tanstack/react-query';
import type { components } from '@adiox/api';
import { useApi } from '@adiox/ui';

export type SymbolInfo = components['schemas']['SymbolInfo'];

/** The symbols the user's pricing group offers, with their contract details. Changes rarely, so it is cached for a while. */
export function useSymbols() {
  const api = useApi();
  return useQuery({
    queryKey: ['symbols'],
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<SymbolInfo[]> => {
      const { data, error } = await api.GET('/api/v1/symbols');
      if (!data) throw new Error(`symbols failed: ${JSON.stringify(error)}`);
      return data;
    },
  });
}

/** Today's daily bar of a symbol (open, high and low), for the change, high and low columns. Null when there is none yet. */
export function useDayBar(symbol: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['day-bar', symbol],
    staleTime: 5 * 60_000,
    refetchInterval: 10 * 60_000,
    queryFn: async (): Promise<{ o: number; h: number; l: number } | null> => {
      const { data } = await api.GET('/api/v1/bars', { params: { query: { symbol, tf: 'D1', count: 1 } } });
      const bar = data?.bars[0];
      return bar ? { o: bar.o, h: bar.h, l: bar.l } : null;
    },
  });
}
